import cx from 'classnames';
import { useRouter } from 'next/router';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';

import useIsMobile from '../../../../../hooks/use-is-mobile';
import NoStock from '../../../../modules/v2/no-stock-message';
import FlowTitleSEO from '../../../../ui/v2/flow-title';
import BPImage from '../../../../ui/v2/image';
import PageTitleSEO from '../../../../ui/v2/typography/page-title';
import useModelVersionLink from '../../../../ui/vehicle-list/use-model-version-link';
import PartPreview from '../../../v2/vehicle/part-preview';
import SetAlertInfo from '../../create-part-alert/index_new';
import { SearchCategoryName } from '../utils/get-category-by-slug';

type VersionsTitleProps = {
  groups: TCategories;
  selection: { category: TCategorySelection };
  vehicle: TCarInfo;
  stock: TResultAPIStocksItem[];
  info: SearchCategoryName;
};

const VersionsTitle = ({
  groups,
  selection,
  vehicle,
  stock,
  info,
}: VersionsTitleProps) => {
  const isMobile = useIsMobile();
  const linkProps = useModelVersionLink({
    isPicker: false,
    have_versions: false,
    slug: vehicle?.model?.slug as string,
  });
  const { t } = useTranslation();
  const {
    query: { locale = 'en' },
  } = useRouter();
  const subCategoryPlural = `parts-and-categories-plural:parts.${selection?.category.category}.${info?.subCategoryEnPart}`;

  return (
    <div>
      <FlowTitleSEO
        isTitleContainsImage={!isMobile}
        imageContainer={
          !isMobile &&
          vehicle && (
            <div
              className={cx(
                'relative',
                'aspect-video',
                'shrink-0',
                'flex basis-5/12',
                'w-full bg-neutral-5'
              )}
            >
              <BPImage
                src={
                  vehicle?.model?.image ||
                  `${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`
                }
                layout="fill" objectFit="contain"
                objectPosition="center"
                fallbackSrc={`${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`}
                className={cx('bg-cover mix-blend-multiply')}
                alt={t('common:images_alt.vehicle_alt', {
                  vehicle: `${vehicle?.brand.name} ${vehicle?.model?.name}`,
                })}
              />
            </div>
          )
        }
        cols={[
          vehicle && (
            <PageTitleSEO
              key="title"
              textAlign={isMobile ? 'center' : 'left'}
              title={
                selection?.category
                  ? t('common:version.pageTitleWithCategory', {
                    brand: vehicle.brand.name,
                    part: info.subCategory,
                    usedWord:
                      info?.subCategoryType && info?.subCategoryType === 'F'
                        ? t('grammar:words.used.feminine')
                        : t('grammar:words.used.masculine'),
                    plural: info?.subCategoryEnPart
                      ? t(
                        subCategoryPlural,
                        {},
                        {
                          fallback: info?.subCategory,
                        }
                      )
                      : info?.subCategory,
                  })
                  : t('common:version.pageTitle', {
                    brand: `${vehicle.brand.name} `,
                  })
              }
              subtitle={t('common:version.pageSubTitle', {
                model: ` ${vehicle.model?.name}`,
                start_date: vehicle.model?.start_date,
                end_date: vehicle.model?.end_date || new Date().getFullYear(),
                usedWord:
                  info?.subCategoryType && info?.subCategoryType === 'F'
                    ? t('grammar:words.used.feminine')
                    : t('grammar:words.used.masculine'),
                plural: selection?.category
                  ? info?.subCategoryEnPart
                    ? t(
                      subCategoryPlural,
                      {},
                      {
                        fallback: info?.subCategory,
                      }
                    )
                    : info?.subCategory
                  : '',
              })}
              categoryAndSubcategory={
                selection?.category && (
                  <PartPreview
                    groups={groups}
                    selection={selection?.category}
                  />
                )
              }
            />
          ),
          vehicle && isMobile && (
            <div
              className={cx(
                'relative',
                'aspect-video',
                'shrink-0',
                'flex basis-5/12',
                'w-full bg-neutral-5'
              )}
            >
              <BPImage
                src={
                  vehicle?.model?.image ||
                  `${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`
                }
                layout="fill" objectFit="contain"
                objectPosition="center"
                fallbackSrc={`${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`}
                className={cx('bg-cover mix-blend-multiply')}
                alt={t('common:images_alt.vehicle_alt', {
                  vehicle: `${vehicle?.brand.name} ${vehicle?.model?.name}`,
                })}
              />
            </div>
          ),
          stock && stock[0]?.stock ? (
            <h2
              key="stock"
              className="max-lg:text-center text-left text-base lg:text-xl"
            >
              <Trans
                i18nKey={
                  selection?.category
                    ? 'common:version.selectFromVersionStockWithPart'
                    : 'common:selectFromVersionStock'
                }
                components={{ strong: <strong /> }}
                values={{
                  brand: vehicle.brand.name + ' ' + vehicle.model?.name,
                  year: `[${vehicle.model?.start_date}-${vehicle.model?.end_date || new Date().getFullYear()
                    }]`,
                  count: stock[0]?.stock.toLocaleString(locale),
                  part: info?.subCategory,
                  vehicle: vehicle.brand.name + ' ' + vehicle.model?.name,
                  yoursWord:
                    info?.subCategoryType && info?.subCategoryType === 'F'
                      ? t('grammar:pronnouns.yours.feminine')
                      : t('grammar:pronnouns.yours.masculine'),
                  theWord:
                    info?.subCategoryType && info?.subCategoryType === 'F'
                      ? t('grammar:pronnouns.the.feminine')
                      : t('grammar:pronnouns.the.masculine'),
                  plural: info?.subCategoryEnPart
                    ? t(
                      subCategoryPlural,
                      {},
                      {
                        fallback: info?.subCategory,
                      }
                    )
                    : info?.subCategory,
                }}
              />
            </h2>
          ) : (
            <div className="">
              <NoStock
                vehicle={vehicle}
                selection={null}
                groups={groups}
                createAlert={false}
              />
            </div>
          ),
          stock && stock[0]?.stock ? (
            <></>
          ) : (
            <div className="max-lg:text-scenter lg:mt-0">
              <SetAlertInfo
                brand={vehicle.brand}
                model={vehicle.model}
                version={vehicle.version}
                part={info?.subCategoryAlertInfo || undefined}
              />
            </div>
          ),
        ]}
      />
    </div>
  );
};

export default VersionsTitle;
